import { Component, type ErrorInfo, type ReactNode } from 'react';

const COMPONENT_STACK_KEY = '__componentStack';

/** `_error` 로 넘어온 에러에서 ComponentStackBoundary 가 붙여 둔 컴포넌트 스택을 꺼낸다. */
export const readComponentStack = (err: unknown): string | undefined => {
  if (typeof err !== 'object' || err === null) return undefined;
  const value = (err as Record<string, unknown>)[COMPONENT_STACK_KEY];
  return typeof value === 'string' ? value : undefined;
};

type State = { error: unknown; rethrow: boolean };

/**
 * 렌더 에러에 React 의 componentStack 을 붙여 Next 의 에러 처리(`_error`)로 다시 넘긴다.
 *
 * Next(pages router)는 componentDidCatch 의 info 를 버리고 err 만 `_error` 로 넘긴다.
 * err.stack 은 React 내부 프레임뿐이라 어느 컴포넌트에서 났는지 알 수 없어서,
 * 여기서 받아 둔 componentStack 을 err 에 실어 보낸다.
 */
class ComponentStackBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, rethrow: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (typeof error === 'object' && error !== null && info.componentStack) {
      (error as Record<string, unknown>)[COMPONENT_STACK_KEY] =
        info.componentStack;
    }
    // componentStack 은 커밋 이후 여기서만 받을 수 있어, 한 번 더 렌더해 바깥(Next)으로 던진다
    this.setState({ rethrow: true });
  }

  render() {
    if (this.state.rethrow) throw this.state.error;
    if (this.state.error) return null;
    return this.props.children;
  }
}

export default ComponentStackBoundary;
